import { useState, useEffect } from 'react';
import { OnboardingTrialStepsType } from '@sdk/authentication/types';
import { IntegrationStatus, PosIntegrations } from '@sdk/integrations/types';
import useConfig from '@sdk/config/useConfig';
import useSlingApi from '@sdk/api/useSlingApi';

const ALL_STEPS_ORDERED = [
  OnboardingTrialStepsType.addEmployees,
  OnboardingTrialStepsType.createShifts,
  OnboardingTrialStepsType.defineWages,
  OnboardingTrialStepsType.setScheduleRules,
  OnboardingTrialStepsType.enableTimeClock,
  OnboardingTrialStepsType.publishSchedule,
  OnboardingTrialStepsType.viewReports,
];

const POS_INTEGRATIONS = Object.keys(PosIntegrations);

/**
 * @returns availableSteps array of steps available for current org in correct order.
 * Some orgs don't have certain steps if they are for example coming from integration).
 */
const useStepsConfig = () => {
  const {
    FEATURE_FLAGS: { ONBOARDING_CHECKLIST_ENABLE_DEFINE_WAGES },
  } = useConfig();
  const api = useSlingApi();
  const [availableSteps, setAvailableSteps] = useState<OnboardingTrialStepsType[]>([]);
  const [integrations, setIntegrations] = useState(null);

  useEffect(() => {
    if (!integrations) {
      const checkIntegrations = async () => {
        try {
          const { data } = await api.integrations.getAvailableIntegrations();
          const activePosIntegrations = data.filter(
            ({ status, type }) =>
              status === IntegrationStatus.enabled && POS_INTEGRATIONS.includes(type)
          );
          setIntegrations(activePosIntegrations);
        } catch (e) {
          // in case of error we will just show all steps, no worries
        }
      };

      checkIntegrations();
    }
  }, [api.integrations, integrations]);

  useEffect(() => {
    const hasActiveIntegrations = Boolean(integrations?.length);
    const filteredSteps = ALL_STEPS_ORDERED.filter((stepKey) => {
      if (
        stepKey === OnboardingTrialStepsType.defineWages &&
        !ONBOARDING_CHECKLIST_ENABLE_DEFINE_WAGES
      ) {
        return false;
      }

      if (hasActiveIntegrations && OnboardingTrialStepsType.enableTimeClock === stepKey) {
        return false;
      }

      return true;
    });

    setAvailableSteps(filteredSteps);
  }, [integrations, ONBOARDING_CHECKLIST_ENABLE_DEFINE_WAGES]);

  return { availableSteps };
};

export default useStepsConfig;
